<script lang="ts">
    /**
     * LandingFooter - 모던 미니멀 푸터
     *
     * 심플한 2단 구조: 링크 + 회사정보
     */
    import { getCompany, formatCopyright } from '../../../lib/company';
    import { getPin } from '../../../lib/pin';
    import { getInk } from '../../../lib/ink';

    interface Props {
        currentPath?: string;
    }

    let { currentPath = '/' }: Props = $props();

    const company = getCompany();

    // 📍 Pin — 꺼진 환경에서는 null.
    const pin = getPin();
    // 🖋️ Ink — 꺼진 환경에서는 null.
    const ink = getInk();

    // 페이지 컨텍스트 (가이드는 JadEye 소속). 가변 구역만 이걸로 분기.
    // 📌 제품 소개 페이지마다 자기 컨텍스트를 갖는다 — 새 서비스가 붙으면 여기 한 줄이다(D9).
    const ctx = $derived(
        currentPath === '/jadeye' || currentPath === '/guide'
            ? 'jadeye'
            : currentPath === '/gridverse'
              ? 'gridverse'
              : pin && currentPath === pin.landing
                ? 'pin'
                : ink && currentPath === ink.landing
                  ? 'ink'
                  : 'company'
    );

    const brandDesc = $derived(
        ctx === 'jadeye'
            ? '함께하는 스마트 건강 관리'
            : ctx === 'gridverse'
              ? '걸어서 개척하는 나만의 차원'
              : ctx === 'pin'
                ? '길 위에서 찾는 진짜 정보'
                : ctx === 'ink' && ink
                  ? ink.tagline
                  : '일상에 모험을 더하는 기술'
    );
</script>

<footer class="landing-footer">
    <div class="footer-inner">
        <!-- Top: Logo + Links + Badges -->
        <!-- 컬럼 수는 '다운로드/사용하기' 구역 유무로 갈린다 — 회사홈·Pin 은 그 구역이 없어 4컬럼이다.
             (클래스명이 is-company 였는데 Pin 이 붙으며 거짓말이 돼 is-compact 로 고쳤다) -->
        <div class="footer-top" class:is-compact={ctx === 'company' || ctx === 'pin' || ctx === 'ink'}>
            <!-- Brand -->
            <div class="footer-brand">
                <a href="/" class="footer-logo">
                    <img src="/logo.svg" alt="JadEye Logo" class="footer-logo-img" />
                    <span class="footer-logo-text">제이드아이</span>
                </a>
                <p class="footer-desc">{brandDesc}</p>
            </div>

            <!-- 가변: 페이지별 링크 그룹 -->
            {#if ctx === 'jadeye'}
                <div class="footer-links-group">
                    <h4 class="footer-links-title">JadEye</h4>
                    <div class="footer-links">
                        <a href="/guide" class="footer-link">사용자 가이드</a>
                        <a
                            href="https://cafe.naver.com/jadeye"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="footer-link">커뮤니티</a
                        >
                    </div>
                </div>
            {:else if ctx === 'gridverse'}
                <!-- 정식 출시(2026-08-28) 전환: 스토어 링크는 JadEye 와 같은 '다운로드' 배지 구역으로 승격.
                     이 자리는 GV 정책 문서 3장(서버 렌더 /gridverse/*) — 이전엔 푸터 어디에도 없던 공백. -->
                <div class="footer-links-group">
                    <h4 class="footer-links-title">Gridverse</h4>
                    <div class="footer-links">
                        <a href="/gridverse/terms" class="footer-link">이용약관</a>
                        <a href="/gridverse/privacy" class="footer-link">개인정보처리방침</a>
                        <a href="/gridverse/account-deletion" class="footer-link">계정 삭제 안내</a>
                    </div>
                </div>
            {:else if ctx === 'pin' && pin}
                <!-- Pin 소개 페이지 — 하위 서비스 **전부**를 나란히.
                     🚨 목록은 서버(pin.axes = PinSeo::nav())가 정한다. 손으로 두 줄 적어 두던 시절
                        타임터널·등산이 발행돼도 여기는 2개뿐이었다(2026-09-08 스위티 지적). -->
                <div class="footer-links-group">
                    <h4 class="footer-links-title">Pin</h4>
                    <div class="footer-links">
                        {#each pin.axes ?? [] as axis (axis.path)}
                            <a href={axis.path} class="footer-link">{axis.label}</a>
                        {/each}
                    </div>
                </div>
            {:else if ctx === 'ink' && ink}
                <!-- Ink 대문 — 서가와 작품 전부. 목록은 서버(ink.works = InkSeo::works())가 정한다. -->
                <div class="footer-links-group">
                    <h4 class="footer-links-title">Ink</h4>
                    <div class="footer-links">
                        <a href={ink.shelf} class="footer-link">서가</a>
                        {#each ink.works as work (work.slug)}
                            <a href={work.path} class="footer-link">{work.label}</a>
                        {/each}
                    </div>
                </div>
            {:else}
                <div class="footer-links-group">
                    <h4 class="footer-links-title">서비스</h4>
                    <div class="footer-links">
                        <a href="/jadeye" class="footer-link">JadEye</a>
                        <a href="/gridverse" class="footer-link">Gridverse</a>
                        {#if pin}
                            <!-- 🚨 서비스 이름은 앱바·카드·푸터에서 **같아야 한다**. 처음엔 검색 키워드를
                                 노리고 여기만 "고속도로 휴게소" 로 뒀는데, 같은 화면에서 앱바는 "Pin",
                                 푸터는 "고속도로 휴게소" 가 되어 **둘이 같은 곳인지 알 수 없었다**(스위티 지적).
                                 키워드 앵커는 크롤러가 읽는 noscript(landing.blade.php)와 카드 본문이 맡는다.
                                 ⇒ 목적지도 다른 두 서비스와 같은 층인 **제품 소개 페이지**다(D9). -->
                            <a href={pin.landing} class="footer-link">Pin</a>
                        {/if}
                        {#if ink}
                            <a href={ink.landing} class="footer-link">Ink</a>
                        {/if}
                    </div>
                </div>
            {/if}

            <!-- 약관: 개인정보처리방침은 공통(법인 차원), 이용약관은 JadEye 전용(서비스 차원).
                 GV 서비스 차원 정책 3장은 위 gridverse ctx 링크 그룹이 맡는다. -->
            <div class="footer-links-group">
                <h4 class="footer-links-title">약관</h4>
                <div class="footer-links">
                    <a href="/privacy" class="footer-link">개인정보처리방침</a>
                    {#if ctx === 'jadeye'}
                        <a href="/terms" class="footer-link">이용약관</a>
                    {/if}
                </div>
            </div>

            <!-- Contact -->
            <div class="footer-links-group">
                <h4 class="footer-links-title">문의</h4>
                <div class="footer-links">
                    <a href="mailto:{company.email.general}" class="footer-link">{company.email.general}</a>
                    {#if company.phone}
                        <span class="footer-text">{company.phone}</span>
                    {/if}
                </div>
            </div>

            <!-- 가변: 사용하기 / 다운로드 (회사홈은 없음) -->
            {#if ctx === 'jadeye'}
                <div id="download" class="footer-links-group">
                    <h4 class="footer-links-title">다운로드</h4>
                    <div class="footer-badges">
                        <a
                            href="https://play.google.com/store/apps/details?id=com.bjf.jadeye"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="footer-badge-link"
                        >
                            <img src="/images/google-play-badge-ko.svg" alt="Google Play" class="footer-badge" />
                        </a>
                        <a
                            href="https://apps.apple.com/kr/app/%EC%A0%9C%EC%9D%B4%EB%93%9C%EC%95%84%EC%9D%B4/id6759511149"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="footer-badge-link"
                        >
                            <img src="/images/app-store-badge-ko.svg" alt="App Store" class="footer-badge appstore" />
                        </a>
                        <a href="/web" class="footer-web-link">
                            웹 브라우저에서 사용하기
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                aria-hidden="true"
                            >
                                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                            </svg>
                        </a>
                    </div>
                </div>
            {:else if ctx === 'gridverse'}
                <!-- JadEye '다운로드' 구역과 동일 위계(배지 2 + 웹 링크) — 정식 출시로 공식 배지 사용 가능해짐 -->
                <div class="footer-links-group">
                    <h4 class="footer-links-title">다운로드</h4>
                    <div class="footer-badges">
                        <a
                            href="https://play.google.com/store/apps/details?id=com.jadeye.gv"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="footer-badge-link"
                        >
                            <img src="/images/google-play-badge-ko.svg" alt="Google Play" class="footer-badge" />
                        </a>
                        <a
                            href="https://apps.apple.com/kr/app/%EA%B7%B8%EB%A6%AC%EB%93%9C%EB%B2%84%EC%8A%A4/id6796878662"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="footer-badge-link"
                        >
                            <img src="/images/app-store-badge-ko.svg" alt="App Store" class="footer-badge appstore" />
                        </a>
                        <a href="/gv" class="footer-web-link">
                            웹으로 사용하기
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                aria-hidden="true"
                            >
                                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                            </svg>
                        </a>
                    </div>
                </div>
            {/if}
        </div>

        <!-- Bottom: Company Info + Venture Mark -->
        <div class="footer-bottom">
            <div class="footer-bottom-info">
                <div class="footer-company">
                    <span>{company.legal_name}</span>
                    <span class="footer-sep">|</span>
                    <span>개인정보보호책임자: {company.privacy_officer.name}</span>
                    <span class="footer-sep">|</span>
                    <span>사업자등록번호: {company.business_number}</span>
                </div>
                <div class="footer-company">
                    {#if company.mail_order_number}
                        <span>통신판매업 신고번호: {company.mail_order_number}</span>
                        <span class="footer-sep">|</span>
                    {/if}
                    <span>{company.address}</span>
                </div>
                {#if ctx === 'ink' && company.publisher_number}
                    <!-- 출판사 신고 — Ink 대문에서만 밝힌다(서버가 그리는 Ink 푸터 ink/partials/site-footer 와 같은 문구) -->
                    <div class="footer-company">
                        <span
                            >출판사 신고번호: {company.publisher_number}{company.publisher_office
                                ? ` (${company.publisher_office})`
                                : ''}</span
                        >
                    </div>
                {/if}
                <p class="footer-copyright">{formatCopyright(company)}</p>
            </div>

            {#if company.venture_certification?.is_certified}
                {@const cert = company.venture_certification}
                <div class="footer-certification">
                    <a
                        href="https://www.smes.go.kr/venturein/pbntc/searchVntrCmp"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="cert-mark-link"
                        aria-label="벤처기업 확인 정보 조회 (중소벤처기업부 벤처인)"
                    >
                        <img
                            src={cert.mark_horizontal}
                            alt="벤처확인기업 Venture Enterprise"
                            class="cert-mark"
                            width="160"
                            height="47"
                            loading="lazy"
                        />
                    </a>
                    <div class="cert-info">
                        <span class="cert-type">{cert.certificate_no}</span>
                    </div>
                </div>
            {/if}
        </div>
    </div>
</footer>

<style>
    .landing-footer {
        background: var(--landing-dark, #1a1a1a);
        color: rgba(255, 255, 255, 0.45);
        padding: 3rem 1.5rem 2rem;
    }

    .footer-inner {
        max-width: 72rem;
        margin: 0 auto;
    }

    /* Top */
    .footer-top {
        display: grid;
        grid-template-columns: 1.4fr 1fr 1fr 1fr 1fr;
        gap: 2rem;
        padding-bottom: 2rem;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }

    /* '다운로드/사용하기' 구역이 없는 컨텍스트(회사홈·Pin)는 4컬럼 */
    .footer-top.is-compact {
        grid-template-columns: 1.4fr 1fr 1fr 1fr;
    }

    /* Brand */
    .footer-brand {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .footer-logo {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        text-decoration: none;
    }

    .footer-logo-img {
        width: 1.75rem;
        height: 1.75rem;
    }

    .footer-logo-text {
        font-size: 1.1rem;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.85);
        letter-spacing: -0.02em;
    }

    .footer-desc {
        font-size: 0.82rem;
        color: rgba(255, 255, 255, 0.35);
    }

    /* Links */
    .footer-links-title {
        font-size: 0.72rem;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.55);
        margin-bottom: 0.6rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
    }

    .footer-links {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
    }

    .footer-link {
        color: rgba(255, 255, 255, 0.4);
        text-decoration: none;
        font-size: 0.85rem;
        transition: color 0.2s;
    }

    .footer-link:hover {
        color: rgba(255, 255, 255, 0.8);
    }

    .footer-text {
        font-size: 0.85rem;
    }

    /* Badges */
    .footer-badges {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
    }

    .footer-badge-link {
        display: flex;
        align-items: center;
        transition: opacity 0.2s;
    }

    .footer-badge-link:hover {
        opacity: 0.8;
    }

    .footer-badge {
        height: 2.5rem;
        object-fit: contain;
    }

    /* 웹 브라우저에서 사용하기 - 보조 링크 */
    .footer-web-link {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        margin-top: 0.4rem;
        padding: 0.35rem 0;
        color: rgba(255, 255, 255, 0.45);
        text-decoration: none;
        font-size: 0.78rem;
        transition:
            color 0.2s,
            gap 0.2s;
    }

    .footer-web-link:hover {
        color: rgba(255, 255, 255, 0.85);
        gap: 0.45rem;
    }

    /* Bottom */
    .footer-bottom {
        padding-top: 1.5rem;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 1.25rem;
    }

    .footer-bottom-info {
        flex: 1 1 auto;
        min-width: 0;
        text-align: left;
    }

    /* Venture Enterprise Certification */
    .footer-certification {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 0.4rem;
        flex-shrink: 0;
    }

    .cert-mark-link {
        display: inline-flex;
        padding: 0.5rem 0.7rem;
        background: #ffffff;
        border-radius: 0.4rem;
        transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    }

    .cert-mark-link:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.22);
    }

    .cert-mark {
        height: auto;
        width: 160px;
        max-width: 100%;
        display: block;
    }

    .cert-info {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 0.3rem;
        font-size: 0.7rem;
        color: rgba(255, 255, 255, 0.45);
    }

    .cert-type {
        color: rgba(255, 255, 255, 0.7);
        font-weight: 600;
    }

    .cert-sep {
        opacity: 0.4;
    }

    .footer-company {
        font-size: 0.72rem;
        line-height: 1.8;
        color: rgba(255, 255, 255, 0.3);
    }

    .footer-sep {
        margin: 0 0.3rem;
        opacity: 0.4;
    }

    .footer-copyright {
        font-size: 0.78rem;
        color: rgba(255, 255, 255, 0.3);
        margin-top: 0.8rem;
    }

    /* Responsive */
    @media (max-width: 768px) {
        .footer-top {
            grid-template-columns: 1fr 1fr;
            gap: 1.5rem;
        }

        .footer-brand {
            grid-column: 1 / -1;
        }

        .footer-company .footer-sep {
            display: none;
        }

        .footer-company span {
            display: block;
        }

        .footer-bottom {
            flex-direction: column;
            align-items: stretch;
            gap: 1.5rem;
        }

        .footer-bottom-info {
            text-align: center;
        }

        .footer-certification {
            align-items: center;
        }

        .cert-info {
            justify-content: center;
        }
    }

    @media (max-width: 480px) {
        .footer-top {
            grid-template-columns: 1fr;
        }
    }

    @media (min-width: 1024px) {
        .landing-footer {
            padding: 3.5rem 2.5rem 2rem;
        }
    }
</style>
